// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LinkRight from "@eisinc/XDL/Icons/LinkRight";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, createLinkIconStyles, hideElementOnPrint } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    description = "description",
    linkAlignment = "linkAlignment",
}

interface QRCodeLabelsProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.description]: string;
        [fields.linkAlignment]: "left" | "center" | "right";
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: getCurrentCartState(state).isLoading,
});

type Props = QRCodeLabelsProps & ReturnType<typeof mapStateToProps>;

export interface QRCodeLabelsStyles {
    wrapper?: InjectableCss;
    qrCodeLabelsHeading?: TypographyProps;
    qrCodeLabelsDescription?: TypographyProps;
    printQRCodeLabelsLink?: LinkPresentationProps;
}

export const qRCodeLabelsStyles: QRCodeLabelsStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 12px;
            border: 1px solid ${color("common.border")};
            ${hideElementOnPrint};
        `,
    },
    qrCodeLabelsHeading: { variant: "h5", as: "p" },
    qrCodeLabelsDescription: {
        ...fontConfig.singleLinexSmall,
        css: css`
            margin-bottom: 16px;
        `,
    },
    printQRCodeLabelsLink: {
        ...createLinkIconStyles({
            icon: LinkRight,
            sizeVariant: "large",
            linkStyles: {
                icon: { position: "right" },
                css: css`
                    ${({ linkAlignment }: any) =>
                        linkAlignment === "right"
                            ? css`
                                  justify-content: flex-start;
                              `
                            : linkAlignment === "center"
                            ? css`
                                  justify-content: center;
                              `
                            : ""}
                `,
            },
        }),
    },
};

const styles = qRCodeLabelsStyles;

const QRCodeLabels = ({ isLoading, fields: { description, label, linkAlignment } }: Props) => {
    return (
        <StyledWrapper
            // Add a section wrapper and use aria-labelledby since there are multiple sections on the same page.
            // Avoid using aria-label because the section already contains a heading.
            as="section"
            aria-labelledby={translate("Print QR Code Labels")}
            {...styles.wrapper}
        >
            {label && <Typography {...styles.qrCodeLabelsHeading}>{label}</Typography>}
            {description && <Typography {...styles.qrCodeLabelsDescription}>{description}</Typography>}
            <Link
                {...styles.printQRCodeLabelsLink}
                disabled={isLoading}
                aria-label={translate("Print QR Code Labels")}
                onClick={() => window.print()}
                {...{ linkAlignment }}
            >
                {translate("Print QR Code Labels")}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(QRCodeLabels),
    definition: {
        displayName: "QR Code Labels",
        group: "Cart",
        icon: "cart-shopping",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                defaultValue: "Speed Up Purchasing With QR Code Labels!",
                fieldType: "Translatable",
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "MultilineTextField",
                defaultValue:
                    "Add products to your cart immediately with QR codes. Print QR code labels for frequently purchased products. You can then scan them with a mobile device by clicking the QR code icon in the eis-inc.com search bar.",
                fieldType: "Translatable",
            },
            {
                name: fields.linkAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
            },
        ],
    },
};

export default widgetModule;
